// Store core shared by every store: one dispatch at a time across all stores,
// and synchronous change notification.

export type Listener<M> = (model: M) => void;

/** A handler returns false to skip the change notification; any other value notifies. */
export type Handler<M, P> = (model: M, payload: P) => unknown;

export interface Store<M extends object> {
  /** A shallow copy of the model, new on every call. */
  getState(): M;
  /** Calls `listener` with the live model after every change; returns the unsubscribe. */
  subscribe(listener: Listener<M>): () => void;
  /** Runs `handler` on the live model inside a dispatch and notifies listeners unless it returns false. */
  dispatch<P>(payload: P, handler: Handler<M, P>): P;
}

let dispatching = false;

function startDispatch() {
  if (dispatching) {
    const error = new Error(
      "Dispatch.dispatch(...): Cannot dispatch in the middle of a dispatch.",
    );
    error.name = "Invariant Violation";
    throw error;
  }
  dispatching = true;
}

/**
 * Runs `run` as a dispatch that no store handles. It still throws when called
 * inside another dispatch.
 */
export function dispatchUnhandled(run?: () => void) {
  startDispatch();
  try {
    run?.();
  } finally {
    dispatching = false;
  }
}

/**
 * Listener list: a listener removed while listeners are being called is skipped
 * and removed after the loop; one added during the loop is first called on the
 * next change.
 */
function createListeners<M>() {
  const listeners: Listener<M>[] = [];
  let publishing = false;
  let removed: Record<number, Listener<M>> = {};

  const remove = (listener: Listener<M>) => {
    const index = listeners.indexOf(listener);
    if (index < 0) {
      return;
    }
    if (publishing) {
      removed[index] = listener;
      return;
    }
    listeners.splice(index, 1);
  };

  const subscribe = (listener: Listener<M>) => {
    listeners.push(listener);
    return () => remove(listener);
  };

  const publish = (model: M) => {
    publishing = true;
    try {
      listeners.forEach((listener, index) => {
        if (!removed[index]) {
          listener(model);
        }
      });
    } finally {
      publishing = false;
      for (const index of Object.keys(removed)) {
        remove(removed[Number(index)]);
      }
      removed = {};
    }
  };

  return { subscribe, publish };
}

export function createStore<M extends object>(model: M): Store<M> {
  const listeners = createListeners<M>();

  return {
    getState: () => ({ ...model }),
    subscribe: listeners.subscribe,
    dispatch<P>(payload: P, handler: Handler<M, P>): P {
      startDispatch();
      try {
        if (handler(model, payload) !== false) {
          listeners.publish(model);
        }
      } finally {
        dispatching = false;
      }
      return payload;
    },
  };
}
